import React from "react";
import { Check, ChevronsUpDown } from "lucide-react";

import { cn } from "lib/utils";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "components/ui/popover";
import { Button } from "components/ui/button";
import { Badge } from "components/ui/badge";

const MultiSelect = ({ options = [], value = [], onChange, placeholder, className }) => {
  const [open, setOpen] = React.useState(false);
  const selectedSet = React.useMemo(() => new Set(value || []), [value]);

  const toggleValue = (val) => {
    const next = new Set(selectedSet);
    if (next.has(val)) {
      next.delete(val);
    } else {
      next.add(val);
    }
    onChange?.(Array.from(next));
  };

  const labels = React.useMemo(() => {
    if (!options.length || selectedSet.size === 0) return [];
    return options.filter((opt) => selectedSet.has(opt.value)).map((opt) => opt.text || opt.label || opt.value);
  }, [options, selectedSet]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="h-auto min-h-10 w-full justify-between"
        >
          <div className="flex flex-wrap items-center gap-2">
            {labels.length === 0 ? (
              <span className="text-sm text-muted-foreground">
                {placeholder || "Select options"}
              </span>
            ) : (
              labels.map((label) => (
                <Badge key={label} variant="secondary" className="px-2 py-0.5">
                  {label}
                </Badge>
              ))
            )}
          </div>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0 bg-white">
        <Command className={cn("bg-white", className)}>
          <CommandInput placeholder="Search..." />
          <CommandList>
            <CommandEmpty>No results found.</CommandEmpty>
            <CommandGroup>
              {options.map((option) => {
                const identifier = String(option.value);
                const isSelected = selectedSet.has(option.value);
                return (
                  <CommandItem
                    key={identifier}
                    value={identifier}
                    onSelect={() => toggleValue(option.value)}
                  >
                    <Check
                      className={cn(
                        "mr-2 h-4 w-4",
                        isSelected ? "opacity-100" : "opacity-0"
                      )}
                    />
                    {option.text || option.label || option.value}
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

export default MultiSelect;

